// @givmo/app-charity — the ways in a director is offered (CBG-SEC2 SEC2-WEB; mockup 2, `m1-signed-out`
// and `m2-door`).
//
// Board's invitation landing and its door place this card; the console supplies it, because signing
// in is the console's (the package owns no sign-in). The same set on both, so the two can never offer
// different ways in, and the three are EQUAL in weight: the charity door leads with email because it
// nudges a nonprofit address, but a director has no address to be nudged toward, and which way is
// right depends only on the account they already have (the approved frames).
//
//   * Google and Microsoft are the console's own provider sign-in, its second-factor branch
//     included (AS-WEB1): a returning account can owe a code.
//   * "Continue with email" opens the console's own email forms in their director arrival: the
//     create form from an invitation (its visitors are mostly new), the sign-in form from the door.
//     From an invitation it also carries the invitation's two facts to the form's lines.
//   * A passkey joins them under an "or", for a director who already has one: Account Security's
//     `CharityPasskeySignIn` (AS-WEB12, the console's own unbound sign-in). The divider and the button
//     show only where the probe says a passkey sign-in can run here (one probe per page load, shared
//     with the button): a passkey is bound to givmo.io (D-AS-10), and the backend's routes are dark
//     until its flag flips.
//   * "Continue with Organization SSO" follows email (AS-WEB30; Board's derivation §6 (3)): the
//     console's organization step in Board's register, shown only where company sign-in can run
//     here. Board's two "use another Givmo account" steps (`another_account`, §6 (5)) leave it out,
//     the lock notice's door included: it would lead straight back to the account just left. Their
//     email opens the sign-in form, as the door's does.
//
// Picking a way in on the invitation is choosing to join (D-CBG-18 answer 3): `onChosen` lets the
// landing record that before the sign-in starts.
//
// The company lock's refusal is ONE notice on this card, whichever way in met it (cut 1007e's fold):
// a provider, the passkey (Board's B-F1: its refusal is handed here, never its own red line), or the
// code prompt a provider sign-in owes (Board's B-F2: with Board's line too). The way back in opens in
// Board's register (Board's B-F3), and "Continue with email" carries Board's line and the arrival
// to the email form (B-F2, B-F4).

import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { Button, Card, CardBody, Icon, ProviderButton } from '@givmo/design-system';
import { secondFactorChallengeOf, type AdminProviderId, type TotpChallenge } from '@givmo/auth';
import type { BoardDirectorWaysInProps } from '@givmo/board';
import {
  ESCAPE_HATCH_LINK_CHARITY,
  EscapeHatchEntryLink,
  OrganizationSsoButton,
  SignInDoorError,
  organizationSsoLabel,
  useOrganizationSsoAvailability,
  usePasskeySignInAvailability,
} from '@givmo/passkeys';
import type { UserCredential } from 'firebase/auth';
import { Mail } from 'lucide-react';
import { useAuth } from '../../auth/auth-context';
import { sanitizeRedirectTarget } from '../../router/redirect-target';
import { CharityPasskeySignIn } from '../auth/passkey-sign-in';
import { TotpChallengePrompt } from '../auth/totp-challenge';
import { useAuthError } from '../auth/use-auth-error';
import { stashDirectorInvitation } from './director-invitation-handoff';
import { carryDirectorLockNoticeLine } from './director-lock-notice-line';
import { DIRECTOR_ROUTES } from './director-routes';

export function CharityDirectorWaysIn({
  arrival,
  returnTo,
  onChosen,
  invitation,
  move,
  lockNoticeLine,
}: BoardDirectorWaysInProps) {
  const { signInWithProvider, probePasskeyAvailability } = useAuth();
  const navigate = useNavigate();
  // Where this door is, for the way back in to return to (AS-WEB26).
  const here = useLocation({ select: (location) => location.href });
  const { error, lockRefused, clearError, surface } = useAuthError();
  const [pendingProvider, setPendingProvider] = useState<AdminProviderId | null>(null);
  const [passkeyPending, setPasskeyPending] = useState(false);
  const [challenge, setChallenge] = useState<TotpChallenge<UserCredential> | null>(null);
  const passkeyAvailable = usePasskeySignInAvailability(probePasskeyAvailability);

  const target = sanitizeRedirectTarget(returnTo) ?? DIRECTOR_ROUTES.home;
  const goBack = useCallback(() => navigate({ to: target }), [navigate, target]);
  // The way back in (AS-WEB26/27), in Board's register (Board's B-F3), returning to this door.
  const openEscapeHatch = useCallback(
    () => void navigate({ to: DIRECTOR_ROUTES.lostCompanyAccess, search: { back: here } }),
    [navigate, here],
  );
  // AS-WEB30: the organization door (Board's §6 (3): the M5 move signs the locked account in through
  // it), only where it can work here — and never on Board's two "use another Givmo account" steps
  // (`another_account`, Board's §6 (5)), where it would lead straight back to the account just left.
  const ssoAvailable = useOrganizationSsoAvailability() && arrival !== 'another_account';
  const openOrganizationSso = useCallback(() => {
    clearError();
    onChosen?.();
    void navigate({ to: DIRECTOR_ROUTES.organizationSso, search: { redirect: target } });
  }, [clearError, onChosen, navigate, target]);
  const organizationSso = ssoAvailable
    ? { label: organizationSsoLabel('continue'), onOpen: openOrganizationSso }
    : undefined;

  const onProvider = useCallback(
    async (provider: AdminProviderId) => {
      clearError();
      onChosen?.();
      setPendingProvider(provider);
      try {
        await signInWithProvider(provider);
        await goBack();
      } catch (thrown) {
        // The first factor was accepted and a second is owed: a state of this sign-in, not a failure.
        const pending = secondFactorChallengeOf<UserCredential>(thrown);
        if (pending !== null) setChallenge(pending);
        else surface(thrown);
      } finally {
        setPendingProvider(null);
      }
    },
    [clearError, onChosen, signInWithProvider, goBack, surface],
  );

  const onEmail = () => {
    clearError();
    onChosen?.();
    if (invitation !== undefined) {
      stashDirectorInvitation({
        organizationName: invitation.organizationName,
        maskedEmailOfRecord: invitation.maskedEmailOfRecord,
      });
    }
    // CBG-SEC2-L4-MOVE (b3 → b3c): the move's two facts, for a director who crosses from the sign-in
    // form to the create form.
    if (move !== undefined) stashDirectorInvitation({ ...move, purpose: 'seat_move' });
    // Board's line goes with the person to the email forms (B-F2): this arrival's, or none.
    carryDirectorLockNoticeLine(lockNoticeLine);
    void navigate({
      to: arrival === 'invitation' ? DIRECTOR_ROUTES.signUpEmail : DIRECTOR_ROUTES.signInEmail,
      // Board's "use another Givmo account" steps: the form's notice offers no organization door
      // either, as this card's doesn't (B-F4).
      search:
        arrival === 'another_account'
          ? { redirect: target, arrival: 'another_account' as const }
          : { redirect: target },
    });
  };

  if (challenge !== null) {
    return (
      <Card className="w-full max-w-[440px]" testId="director-sign-in-challenge">
        <CardBody className="flex flex-col gap-16">
          <TotpChallengePrompt
            challenge={challenge}
            onResolved={goBack}
            onCancel={() => setChallenge(null)}
            onUnlink={openEscapeHatch}
            organizationSso={organizationSso}
            lockNoticeLine={lockNoticeLine}
          />
        </CardBody>
      </Card>
    );
  }

  const busy = pendingProvider !== null || passkeyPending;
  return (
    <Card className="w-full max-w-[440px]" testId="director-sign-in-choices">
      <CardBody className="flex flex-col gap-12">
        {/* The console door's testids, so one e2e selector drives a provider on either door. */}
        <ProviderButton
          provider="google"
          label="Continue with Google"
          loading={pendingProvider === 'google'}
          disabled={busy}
          onClick={() => void onProvider('google')}
          testId="login-google-button"
        />
        <ProviderButton
          provider="microsoft"
          label="Continue with Microsoft"
          loading={pendingProvider === 'microsoft'}
          disabled={busy}
          onClick={() => void onProvider('microsoft')}
          testId="login-microsoft-button"
        />
        <Button
          appearance="saas"
          tone="secondary"
          size="lg"
          fullWidth
          leadingIcon={<Icon icon={Mail} size={18} aria-hidden />}
          className="justify-center gap-10 font-w600"
          disabled={busy}
          onClick={onEmail}
          testId="director-continue-email"
        >
          Continue with email
        </Button>
        {/* AS-WEB30: after the personal ways in (frame L2); a passkey keeps its own "or" below. */}
        <OrganizationSsoButton
          verb="continue"
          available={ssoAvailable}
          disabled={busy}
          onOpen={openOrganizationSso}
        />
        {passkeyAvailable ? (
          <>
            <div className="flex items-center gap-12 py-4" aria-hidden>
              <span className="h-px flex-1 bg-border-subtle" />
              <span className="font-sans text-s12 font-w500 tracking-normal text-text-tertiary">
                or
              </span>
              <span className="h-px flex-1 bg-border-subtle" />
            </div>
            <CharityPasskeySignIn
              returnTo={target}
              onChosen={() => {
                clearError();
                onChosen?.();
              }}
              onPendingChange={setPasskeyPending}
              disabled={pendingProvider !== null}
              onLockRefused={surface}
            />
          </>
        ) : null}
        {/* AS-WEB27: the company lock's refusal is the notice (frame B6), with Board's one line
            where Board gives one (from an invitation: frame a0); anything else the red line. */}
        <SignInDoorError
          error={error}
          lockRefused={lockRefused}
          onUnlink={openEscapeHatch}
          organizationSso={organizationSso}
          line={lockNoticeLine}
          testId="director-sign-in-error"
        />
        {/* AS-WEB26: the way back in for someone their company locks out, at the card's foot, only
            where a lock exists here. It opens the console's own flow, which returns to this door,
            and steps aside while the notice carries it. */}
        {!(lockRefused && error !== null) && (
          <EscapeHatchEntryLink className={ESCAPE_HATCH_LINK_CHARITY} onOpen={openEscapeHatch} />
        )}
      </CardBody>
    </Card>
  );
}
